import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bug, Package, Sparkles, Wrench } from 'lucide-react';
import { normalizeReleaseChanges, ReleaseChangeSections } from '../../services/appReleaseService';
import { useAppLocale } from '../../providers/LocaleProvider';

const CHANGE_GROUP_STYLES: Record<
  'features' | 'modules' | 'bugFixes' | 'improvements',
  { box: string; label: string }
> = {
  features: {
    box: 'border-violet-200/90 bg-violet-50/70 dark:border-violet-800/50 dark:bg-violet-950/25',
    label: 'text-violet-800 dark:text-violet-200',
  },
  modules: {
    box: 'border-sky-200/90 bg-sky-50/70 dark:border-sky-800/50 dark:bg-sky-950/25',
    label: 'text-sky-800 dark:text-sky-200',
  },
  bugFixes: {
    box: 'border-rose-200/90 bg-rose-50/70 dark:border-rose-800/50 dark:bg-rose-950/25',
    label: 'text-rose-800 dark:text-rose-200',
  },
  improvements: {
    box: 'border-emerald-200/90 bg-emerald-50/70 dark:border-emerald-800/50 dark:bg-emerald-950/25',
    label: 'text-emerald-800 dark:text-emerald-200',
  },
};

const ChangeGroup: React.FC<{
  kind: keyof typeof CHANGE_GROUP_STYLES;
  icon: React.ReactNode;
  label: string;
  items: string[];
  compact?: boolean;
  prose?: boolean;
}> = ({ kind, icon, label, items, compact = false, prose = false }) => {
  const { direction } = useAppLocale();
  if (!items.length) return null;
  const styles = CHANGE_GROUP_STYLES[kind];
  return (
    <div className={`rounded-lg border px-3 ${compact ? 'py-2' : 'py-2.5'} ${styles.box}`} dir={direction}>
      <div
        className={`mb-2 flex items-center gap-1.5 text-start text-xs font-semibold uppercase tracking-wide ${styles.label}`}
      >
        {icon}
        {label}
      </div>
      <ul
        className={`space-y-1.5 text-slate-700 dark:text-slate-300 ${
          prose
            ? 'text-justify text-[10px] leading-snug sm:text-[11px]'
            : `text-start leading-relaxed ${compact ? 'text-xs' : 'text-sm'}`
        }`}
      >
        {items.map((item) => (
          <li key={item} className={prose ? 'w-full text-justify' : 'flex gap-2'}>
            {prose ? null : <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-amber-500/80" aria-hidden />}
            <span className={prose ? 'block w-full text-justify' : undefined} style={prose ? { textAlign: 'justify' } : undefined}>
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const emptyReleaseChanges = (): ReleaseChangeSections => ({
  features: [],
  modules: [],
  bugFixes: [],
  improvements: [],
});

export const hasReleaseChangeDetails = (changes?: ReleaseChangeSections | null): boolean => {
  const c = normalizeReleaseChanges(changes);
  return (
    c.features.length + c.modules.length + c.bugFixes.length + c.improvements.length > 0
  );
};

export interface ReleaseChangeGroupsProps {
  changes?: ReleaseChangeSections | null;
  className?: string;
  compact?: boolean;
  /** Smaller, justified lines for the version journey window. */
  prose?: boolean;
}

export const ReleaseChangeGroups: React.FC<ReleaseChangeGroupsProps> = ({
  changes,
  className = '',
  compact = false,
  prose = false,
}) => {
  const { t } = useTranslation('nav');
  const { locale } = useAppLocale();
  const c = normalizeReleaseChanges(changes);
  if (!hasReleaseChangeDetails(c)) return null;

  const isRtl = locale === 'fa';

  return (
    <div className={`space-y-3 ${className}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <ChangeGroup
        kind="features"
        icon={<Sparkles className="h-3.5 w-3.5" />}
        label={t('releaseFeatures')}
        items={c.features}
        compact={compact}
        prose={prose}
      />
      <ChangeGroup
        kind="modules"
        icon={<Package className="h-3.5 w-3.5" />}
        label={t('releaseModules')}
        items={c.modules}
        compact={compact}
        prose={prose}
      />
      <ChangeGroup
        kind="bugFixes"
        icon={<Bug className="h-3.5 w-3.5" />}
        label={t('releaseBugFixes')}
        items={c.bugFixes}
        compact={compact}
        prose={prose}
      />
      <ChangeGroup
        kind="improvements"
        icon={<Wrench className="h-3.5 w-3.5" />}
        label={t('releaseImprovements')}
        items={c.improvements}
        compact={compact}
        prose={prose}
      />
    </div>
  );
};
